import React, { useState } from "react";
import { Box, Button, Icon, Menu, MenuItem, Text } from "@showitinc/showit-wc";
import cx from "classnames";

import styles from "./Footer.module.css";
import AppConstants from "../../constants/AppConstants";
import AppConfig from "../../config/app";
import UIActions from "../../actions/UIActions";
import FooterMiddleSection from "./FooterMiddleSection";
import { trackEvent } from "../../utils/TagUtils";
import DocumentStore from "../../document/DocumentStore";
import { COMMANDS } from "../../document/commands";
import { useAccountState, isContributing as getIsContributing } from "../../hooks/query/useAccount";
import UserModal from "../../components/UserModal";
import InfoSection from "../../components/InfoSection";
import {
  STAGE_OVERLAY_MOBILE,
  STAGE_OVERLAY_GUIDES,
  STAGE_OVERLAY_COLUMN,
  STAGE_OVERLAY_CENTER,
  STAGE_OVERLAY_SNAP,
  ZOOM_INCREMENT,
} from "../../constants/UIConstants";
import ViewZoomMenu from "../toolbar/ViewZoomMenu";
import DesignViewStore from "../../stores/DesignViewStore";

const { STAGE_VIEW_SPLIT, STAGE_VIEW_MOBILE, STAGE_VIEW_DESKTOP } = AppConstants;

const Footer = ({ view, blockContainer }) => {
  const stageView = view.getIn(["stage", "view"]);
  const [showUserModal, setShowUserModal] = useState(false);

  const splitActive = stageView === STAGE_VIEW_SPLIT;
  const mobileActive = stageView === STAGE_VIEW_MOBILE;
  const desktopActive = stageView === STAGE_VIEW_DESKTOP;

  const _changeStageView = (newView) => {
    if (newView === stageView) return;
    UIActions.changeStageView(newView);
    if (newView === STAGE_VIEW_DESKTOP || newView === STAGE_VIEW_MOBILE) {
      UIActions.selectLayout({ layout: newView, from: "panel" });
    }
  };

  const zoomInOut = (increment) => {
    const layout = blockContainer.get("layout");
    UIActions.incrementZoom(increment, layout);
  };

  const _undo = () => {
    DocumentStore.dispatch({ type: COMMANDS.UNDO });
    trackEvent("button_click", { button_name: "undo", button_location: "top_toolbar" });
  };

  const _redo = () => {
    DocumentStore.dispatch({ type: COMMANDS.REDO });
    trackEvent("button_click", { button_name: "redo", button_location: "top_toolbar" });
  };

  const _toggleOverlay = (overlayType) => {
    UIActions.toggleStageOverlay(overlayType);
  };

  const hasCanvasGuides = view.getIn(["stage", "overlays", STAGE_OVERLAY_GUIDES], false);
  const hasMobileOverlay = view.getIn(["stage", "overlays", STAGE_OVERLAY_MOBILE], false);
  const hasColumnGuides = view.getIn(["stage", "overlays", STAGE_OVERLAY_COLUMN], false);
  const hasCenterGuides = view.getIn(["stage", "overlays", STAGE_OVERLAY_CENTER], false);
  const hasSnapping = view.getIn(["stage", "overlays", STAGE_OVERLAY_SNAP], false);

  const user = useAccountState();
  const isContributing = getIsContributing();
  const displayName = isContributing
    ? `${user.getIn(["actingContributor", "first_name"])} ${user.getIn([
        "actingContributor",
        "last_name",
      ])} (${user.get("first_name")} ${user.get("last_name")})`
    : `${user.get("first_name")} ${user.get("last_name")}`;

  const showModal = () => {
    if (AppConfig.demoMode) {
      return;
    }
    setShowUserModal(true);
  };

  return (
    <footer className={styles.footer} data-intercom-target="design view footer">
      <div className={styles["footer-side"]}>
        <Box
          className={styles["footer-account-container"]}
          data-intercom-target="design profile footer"
        >
          <button type="button" className={styles.accountButton} onClick={showModal}>
            <Icon type="accountBox" size="20" />
            <Text className={styles["footer-account-name-text"]}>{displayName}</Text>
          </button>
        </Box>

        <Box className={styles["left-footer-controls-group"]} data-intercom-target="stage controls">
          <Box className={styles["layout-chooser-toggle-group"]}>
            <Button
              variant="icon"
              iconSize="20"
              className={cx([
                styles["toggle-group-option"],
                styles["split-view-option"],
                splitActive && styles["toggle-group-option-active"],
              ])}
              iconPrefix="screenDevices"
              onSIClick={() => _changeStageView(STAGE_VIEW_SPLIT)}
            />
            <Button
              variant="icon"
              iconSize="20"
              className={cx([
                styles["toggle-group-option"],
                mobileActive && styles["toggle-group-option-active"],
              ])}
              active={mobileActive}
              iconPrefix="mobileDevice"
              onSIClick={() => _changeStageView(STAGE_VIEW_MOBILE)}
            />
            <Button
              variant="icon"
              iconSize="20"
              className={cx([
                styles["toggle-group-option"],
                desktopActive && styles["toggle-group-option-active"],
              ])}
              iconPrefix="desktopDevice"
              onSIClick={() => _changeStageView(STAGE_VIEW_DESKTOP)}
            />
          </Box>
          <Menu variant="icon" py="lg">
            <Button
              variant="icon"
              iconSize="20"
              className={styles["guides-button"]}
              slot="trigger-element"
              iconPrefix="guides"
            />

            <MenuItem
              icon={hasMobileOverlay ? "check" : ""}
              keepOpen
              onClick={() => _toggleOverlay(STAGE_OVERLAY_MOBILE)}
            >
              Mobile overlay
            </MenuItem>
            <MenuItem
              icon={hasCanvasGuides ? "check" : ""}
              keepOpen
              onClick={() => _toggleOverlay(STAGE_OVERLAY_GUIDES)}
            >
              Canvas guidelines
            </MenuItem>
            <MenuItem
              icon={hasColumnGuides ? "check" : ""}
              keepOpen
              onClick={() => _toggleOverlay(STAGE_OVERLAY_COLUMN)}
            >
              Column guidelines
            </MenuItem>
            <MenuItem
              icon={hasCenterGuides ? "check" : ""}
              keepOpen
              onClick={() => _toggleOverlay(STAGE_OVERLAY_CENTER)}
            >
              Center guidelines
            </MenuItem>
            <MenuItem
              variant="icon-hotkey"
              icon={hasSnapping ? "check" : ""}
              keepOpen
              onClick={() => _toggleOverlay(STAGE_OVERLAY_SNAP)}
            >
              Enable snapping
            </MenuItem>
          </Menu>

          <Box
            className={styles["layout-chooser-toggle-group-zoom"]}
            data-intercom-target="design footer zoom"
          >
            <Box className={styles["layout-chooser-toggle-group-zoom-buttons"]}>
              <Button
                variant="icon"
                className={styles["toggle-group-option"]}
                iconPrefix="zoomOut"
                title="Zoom Out"
                onSIClick={() => zoomInOut(-ZOOM_INCREMENT)}
              />
              <ViewZoomMenu viewState={view} blockContainer={blockContainer} />
              <Button
                variant="icon"
                className={styles["toggle-group-option"]}
                iconPrefix="zoomIn"
                title="Zoom In"
                onSIClick={() => zoomInOut(ZOOM_INCREMENT)}
              />
            </Box>
          </Box>
        </Box>
      </div>
      <FooterMiddleSection blockContainer={blockContainer} />
      <div className={cx(styles["footer-side"], styles["footer-side-end"])}>
        <Box
          d="flex"
          className={styles["right-toolbar-button-group"]}
          data-intercom-target="undo and redo"
        >
          <Button
            variant="icon"
            iconSize="20"
            icon-prefix="undo"
            onClick={_undo}
            className={styles["right-toolbar-button"]}
          />
          <Button
            variant="icon"
            iconSize="20"
            icon-prefix="redo"
            onClick={_redo}
            className={styles["right-toolbar-button"]}
          />
        </Box>
        <InfoSection />
      </div>
      <UserModal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        user={user}
        name={displayName}
        container={this}
      />
    </footer>
  );
};

export default Footer;
